import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  FiEdit2,
  FiPlus,
  FiSave,
  FiTrash2,
  FiUserCheck,
  FiUsers,
  FiX,
} from "react-icons/fi";
import supportService from "../../api/services/supportService";
import { confirmDanger } from "../../utils/confirmDialog";
import AdminPanelUserModal from "../../Modals/AdminPanelUserModal";
import {
  DEFAULT_CHAT_SUPPORT_PERMS,
  DEFAULT_LEAD_PERMS,
  DEFAULT_MEMBER_PERMS,
  TAB_PERM_LABELS,
  defaultsForPanelRole,
  tabLabelsForPanelRole,
} from "../../constants/supportTabPermissions";
import { CardsSkeleton, TableSkeleton } from "../../components/common/skeletons";

const THEME = { primary: "#FB565F" };

const SUPPORT_PANEL_ROLES = new Set(["CSR", "CSR Team Lead", "Chat Support"]);
const MEMBER_PANEL_ROLES = new Set(["CSR", "Chat Support"]);

const PANEL_ROLE_STYLES = {
  Admin: "bg-purple-100 text-purple-800",
  Developer: "bg-orange-100 text-orange-800",
  Author: "bg-blue-100 text-blue-800",
  CSR: "bg-teal-100 text-teal-800",
  "CSR Team Lead": "bg-indigo-100 text-indigo-800",
  "Chat Support": "bg-cyan-100 text-cyan-800",
};

const SUPPORT_ROLE_STYLES = {
  SUPERVISOR: "bg-red-100 text-red-800",
  TEAM_LEAD: "bg-indigo-100 text-indigo-800",
  AGENT: "bg-emerald-100 text-emerald-800",
  NONE: "bg-gray-100 text-gray-500",
};

const TICKET_STATUS_COLUMNS = [
  { key: "PENDING", label: "Pending", badge: "bg-amber-100 text-amber-800" },
  { key: "IN_PROGRESS", label: "In Progress", badge: "bg-blue-100 text-blue-800" },
  { key: "COMPLETED", label: "Completed", badge: "bg-purple-100 text-purple-800" },
  { key: "RESOLVED", label: "Resolved", badge: "bg-emerald-100 text-emerald-800" },
  { key: "CLOSED", label: "Closed", badge: "bg-gray-100 text-gray-700" },
];

const OUTREACH_COLUMNS = [
  { key: "pending", label: "Pending", badge: "bg-amber-100 text-amber-800" },
  { key: "contacted", label: "Contacted", badge: "bg-blue-100 text-blue-800" },
  { key: "completed", label: "Completed", badge: "bg-purple-100 text-purple-800" },
  { key: "resolved", label: "Resolved", badge: "bg-emerald-100 text-emerald-800" },
];

const CHAT_LOG_COLUMNS = [
  { key: "chatMessages", label: "Chats", badge: "bg-cyan-100 text-cyan-800" },
  { key: "emailsAdded", label: "Emails", badge: "bg-blue-100 text-blue-800" },
  { key: "socialResponses", label: "Social", badge: "bg-purple-100 text-purple-800" },
  { key: "randomRequests", label: "Random", badge: "bg-emerald-100 text-emerald-800" },
];

const inputCls =
  "border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none";

function emptyTeamForm() {
  return {
    name: "",
    team_type: "csr",
    team_lead_id: "",
    member_ids: [],
    lead_tab_permissions: { ...DEFAULT_LEAD_PERMS },
    member_tab_permissions: { ...DEFAULT_MEMBER_PERMS },
    chat_support_tab_permissions: { ...DEFAULT_CHAT_SUPPORT_PERMS },
    member_permissions: {},
  };
}

const TEAM_TYPE_OPTIONS = [
  {
    id: "csr",
    label: "CSR / Sale Team",
    description: "CSR outreach, tickets, and sale agents",
  },
  {
    id: "chat_support",
    label: "Chat Support Team",
    description: "Chat support log, daily targets, and chat agents",
  },
];

function TeamTypeSelector({ value, onChange, disabled = false }) {
  return (
    <div className="mb-4">
      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
        Team type <span className="text-red-500">*</span>
      </p>
      <div className="flex flex-wrap gap-3">
        {TEAM_TYPE_OPTIONS.map((opt) => {
          const active = value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              disabled={disabled}
              onClick={() => onChange(opt.id)}
              className={`rounded-xl border px-4 py-3 text-left min-w-[200px] transition-colors ${
                active
                  ? "border-rose-300 bg-rose-50 ring-2 ring-rose-100"
                  : "border-gray-200 bg-white hover:border-gray-300"
              } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <p className={`text-sm font-semibold ${active ? "text-rose-800" : "text-gray-900"}`}>
                {opt.label}
              </p>
              <p className="text-[11px] text-gray-500 mt-1">{opt.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function summarizeTabs(perms) {
  if (!perms) return "—";
  const labels = TAB_PERM_LABELS.filter((t) => perms[t.key]).map((t) => t.label);
  return labels.length ? labels.join(", ") : "None";
}

function TabPermissionsEditor({ title, value, onChange, labels = TAB_PERM_LABELS }) {
  const perms = value || {};
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4">
      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">{title}</p>
      <div className="flex flex-wrap gap-4">
        {labels.map((tab) => (
          <label key={tab.key} className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={!!perms[tab.key]}
              onChange={(e) => onChange({ ...perms, [tab.key]: e.target.checked })}
              className="accent-red-500"
            />
            {tab.label}
          </label>
        ))}
      </div>
    </div>
  );
}

function MemberTabOverrides({
  memberIds,
  staff,
  memberPermissions,
  onChangeMemberPerms,
}) {
  const selected = useMemo(() => {
    const map = Object.fromEntries(staff.map((s) => [String(s.id), s]));
    return memberIds.map((id) => map[String(id)]).filter(Boolean);
  }, [memberIds, staff]);

  if (!selected.length) return null;

  return (
    <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4 space-y-3">
      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
        Per-member tab access (optional overrides)
      </p>
      <p className="text-[11px] text-gray-500">
        Leave unchecked overrides to use the role defaults above (CSR vs Chat Support). Set tabs per person when needed.
      </p>
      {selected.map((u) => {
        const id = String(u.id);
        const role = u.panelRole || "CSR";
        const labels = tabLabelsForPanelRole(role);
        const value = memberPermissions[id] || defaultsForPanelRole(role);
        const hasOverride = !!memberPermissions[id];
        return (
          <div key={id} className="rounded-lg border border-white bg-white p-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-900">
                  {u.firstName} {u.lastName}
                </p>
                <PanelRoleBadge role={role} />
              </div>
              <label className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasOverride}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChangeMemberPerms({
                        ...memberPermissions,
                        [id]: defaultsForPanelRole(role),
                      });
                    } else {
                      const next = { ...memberPermissions };
                      delete next[id];
                      onChangeMemberPerms(next);
                    }
                  }}
                  className="accent-indigo-600"
                />
                Custom tabs
              </label>
            </div>
            {hasOverride && (
              <div className="flex flex-wrap gap-3">
                {labels.map((tab) => (
                  <label key={tab.key} className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!value[tab.key]}
                      onChange={(e) =>
                        onChangeMemberPerms({
                          ...memberPermissions,
                          [id]: { ...value, [tab.key]: e.target.checked },
                        })
                      }
                      className="accent-red-500"
                    />
                    {tab.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PanelRoleBadge({ role }) {
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
        PANEL_ROLE_STYLES[role] || "bg-gray-100 text-gray-600"
      }`}
    >
      {role}
    </span>
  );
}

function SupportRoleBadge({ role, label }) {
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
        SUPPORT_ROLE_STYLES[role] || SUPPORT_ROLE_STYLES.NONE
      }`}
    >
      {label || role}
    </span>
  );
}

function SectionCard({ title, subtitle, icon: Icon, accent = "white", children, action }) {
  const accents = {
    white: "bg-white border-gray-200",
    rose: "bg-gradient-to-br from-rose-50/80 to-white border-rose-100",
    indigo: "bg-gradient-to-br from-indigo-50/60 to-white border-indigo-100",
  };
  return (
    <div className={`rounded-2xl border shadow-sm overflow-hidden ${accents[accent] || accents.white}`}>
      <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-gray-900 text-base flex items-center gap-2">
            {Icon && <Icon className="shrink-0" style={{ color: THEME.primary }} />}
            {title}
          </h2>
          {subtitle && <p className="text-xs text-gray-500 mt-1 max-w-2xl">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function MemberPickerTable({
  teamLeadChoices,
  memberChoices,
  teamLeadId,
  memberIds,
  onLeadChange,
  onToggleMember,
  search,
  onSearchChange,
  emptyMessage,
  leadLocked = false,
  highlightLeadRequired = false,
}) {
  const q = search.trim().toLowerCase();
  const filterUser = (u) =>
    !q ||
    `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
    (u.email || "").toLowerCase().includes(q) ||
    (u.panelRole || "").toLowerCase().includes(q);

  const leads = teamLeadChoices.filter(filterUser);
  const members = memberChoices.filter(filterUser);

  return (
    <div className="space-y-3">
      <input
        type="text"
        placeholder="Search by name, email, or role..."
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        className={`${inputCls} w-full max-w-md`}
      />
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-xs md:text-sm">
          <thead className="bg-gray-50/90">
            <tr>
              <th
                className={`px-3 py-2.5 text-left font-semibold w-14 ${
                  highlightLeadRequired ? "text-amber-700 bg-amber-50" : "text-gray-500"
                }`}
              >
                Lead{highlightLeadRequired ? " *" : ""}
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 w-14">Member</th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500">User</th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500">Team</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((u) => (
              <tr key={`lead-${u.id}`} className="border-t border-gray-100 bg-indigo-50/40">
                <td className="px-3 py-2.5 text-center">
                  <input
                    type="radio"
                    name="team_lead_picker"
                    checked={String(teamLeadId) === String(u.id)}
                    onChange={() => onLeadChange(u.id)}
                    disabled={leadLocked}
                    className="accent-indigo-600"
                  />
                </td>
                <td className="px-3 py-2.5 text-center text-gray-300">—</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                      style={{ backgroundColor: THEME.primary }}
                    >
                      {(u.firstName || "C").charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {u.firstName} {u.lastName}
                      </p>
                      <p className="text-[10px] text-gray-400">{u.email}</p>
                    </div>
                    <PanelRoleBadge role={u.panelRole} />
                    {u.teamName ? (
                      <span className="text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded">
                        Also leads {u.teamName}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-gray-500">{u.teamName || "—"}</td>
              </tr>
            ))}
            {members.map((u) => (
              <tr key={`member-${u.id}`} className="border-t border-gray-100 hover:bg-gray-50/60">
                <td className="px-3 py-2.5 text-center text-gray-300">—</td>
                <td className="px-3 py-2.5 text-center">
                  <input
                    type="checkbox"
                    checked={memberIds.includes(String(u.id))}
                    onChange={() => onToggleMember(u.id)}
                    disabled={String(teamLeadId) === String(u.id)}
                    className="accent-teal-600"
                  />
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                        u.panelRole === "Chat Support"
                          ? "bg-cyan-100 text-cyan-700"
                          : "bg-teal-100 text-teal-700"
                      }`}
                    >
                      {(u.firstName || "C").charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {u.firstName} {u.lastName}
                      </p>
                      <p className="text-[10px] text-gray-400">{u.email}</p>
                    </div>
                    <PanelRoleBadge role={u.panelRole} />
                  </div>
                </td>
                <td className="px-3 py-2.5 text-gray-500">{u.teamName || "—"}</td>
              </tr>
            ))}
            {!leads.length && !members.length && (
              <tr>
                <td colSpan={4} className="px-3 py-10 text-center text-gray-400">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-gray-400">
        Team lead must be a CSR Team Lead (select the Lead radio). One lead can manage multiple
        teams — e.g. a CSR team and a Chat Support team. Members can be CSR or Chat Support.
      </p>
    </div>
  );
}

function RoleTabEditors({ form, setForm, teamType = "csr" }) {
  const isChatTeam = teamType === "chat_support";
  const leadLabels = isChatTeam
    ? TAB_PERM_LABELS.filter((t) =>
        ["tickets", "performance", "teams", "chat_support_log", "agent_reports"].includes(t.key)
      )
    : TAB_PERM_LABELS.filter((t) => t.key !== "chat_support_log");

  return (
    <div className={`grid gap-4 ${isChatTeam ? "md:grid-cols-2" : "md:grid-cols-2"}`}>
      <TabPermissionsEditor
        title="Team lead tab access"
        value={form.lead_tab_permissions}
        onChange={(lead_tab_permissions) => setForm((f) => ({ ...f, lead_tab_permissions }))}
        labels={leadLabels}
      />
      {isChatTeam ? (
        <TabPermissionsEditor
          title="Chat Support member default tabs"
          value={form.chat_support_tab_permissions}
          onChange={(chat_support_tab_permissions) =>
            setForm((f) => ({ ...f, chat_support_tab_permissions }))
          }
          labels={tabLabelsForPanelRole("Chat Support")}
        />
      ) : (
        <TabPermissionsEditor
          title="CSR member default tabs"
          value={form.member_tab_permissions}
          onChange={(member_tab_permissions) => setForm((f) => ({ ...f, member_tab_permissions }))}
          labels={tabLabelsForPanelRole("CSR")}
        />
      )}
    </div>
  );
}

function inferTeamPerformanceKind(team) {
  if (team?.teamType === "chat_support" || team?.performanceKind === "chat_support") {
    return "chat_support";
  }
  if (team?.teamType === "csr" || team?.performanceKind === "csr") {
    return "csr";
  }
  const roles = (team?.members || []).map((m) => m.panelRole).filter(Boolean);
  const hasChat = roles.some((r) => r === "Chat Support");
  const hasCsr = roles.some((r) => r === "CSR");
  if (hasChat && !hasCsr) return "chat_support";
  if (hasCsr && !hasChat) return "csr";
  const name = (team?.name || "").toLowerCase();
  if (name.includes("chat")) return "chat_support";
  if (name.includes("sale") || name.includes("csr")) return "csr";
  return hasChat ? "chat_support" : "csr";
}

function sortPerformanceRows(rows = []) {
  return [...rows].sort((a, b) => {
    if (a.isTeamLead && !b.isTeamLead) return -1;
    if (!a.isTeamLead && b.isTeamLead) return 1;
    return (a.name || "").localeCompare(b.name || "");
  });
}

function TeamMemberStatsTable({ performance = [], teamType = "csr" }) {
  const rows = useMemo(() => {
    const all = performance || [];
    const filtered =
      teamType === "chat_support"
        ? all.filter(
            (p) =>
              p.isTeamLead ||
              p.panelRole === "Chat Support" ||
              (Number(p.counts?.total) || 0) > 0 ||
              (p.chatSupportLog?.customerLogCount || 0) > 0 ||
              (p.chatSupportLog?.daysReported || 0) > 0
          )
        : all.filter(
            (p) =>
              p.isTeamLead ||
              p.panelRole === "CSR" ||
              p.panelRole === "CSR Team Lead" ||
              (p.csrOutreachCounts?.total || 0) > 0
          );
    return sortPerformanceRows(filtered);
  }, [performance, teamType]);

  const isChatTeam = teamType === "chat_support";

  if (!rows.length) {
    return (
      <p className="text-sm text-gray-400 text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200">
        No {isChatTeam ? "chat support" : "CSR / sale"} activity for this team yet.
      </p>
    );
  }

  if (isChatTeam) {
    const ticketColSpan = 1 + TICKET_STATUS_COLUMNS.length;
    const chatColSpan = 2 + CHAT_LOG_COLUMNS.length;
    return (
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-xs md:text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 border-r border-gray-200">
                Agent
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 border-r border-gray-200">
                Role
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 border-r border-gray-200 min-w-[180px]">
                Can access
              </th>
              <th
                colSpan={ticketColSpan}
                className="px-3 py-2 text-center font-semibold text-gray-700 bg-red-50/80"
              >
                Support tickets
              </th>
              <th
                colSpan={chatColSpan}
                className="px-3 py-2 text-center font-semibold text-gray-700 bg-cyan-50/80"
              >
                Chat support (this month)
              </th>
            </tr>
            <tr>
              <th colSpan={3} className="border-r border-gray-200" />
              <th className="px-2 py-2 text-center font-semibold text-gray-500 bg-red-50/50">Total</th>
              {TICKET_STATUS_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="px-2 py-2 text-center font-semibold text-gray-500 bg-red-50/50 border-r border-gray-100"
                >
                  {col.label}
                </th>
              ))}
              <th className="px-2 py-2 text-center font-semibold text-gray-500 bg-cyan-50/50">Customers</th>
              <th className="px-2 py-2 text-center font-semibold text-gray-500 bg-cyan-50/50">Target %</th>
              {CHAT_LOG_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="px-2 py-2 text-center font-semibold text-gray-500 bg-cyan-50/50 border-r border-gray-100"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const c = p.counts || {};
              return (
              <tr key={p.agentId} className="border-t border-gray-100 hover:bg-gray-50/50">
                <td className="px-3 py-2.5 border-r border-gray-100">
                  <p className="font-medium text-gray-900 whitespace-nowrap">{p.name}</p>
                  <p className="text-[10px] text-gray-400 break-all">{p.email}</p>
                </td>
                <td className="px-3 py-2.5 border-r border-gray-100">
                  <div className="flex flex-wrap items-center gap-1">
                    {p.isTeamLead ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-indigo-100 text-indigo-800">
                        Team Lead
                      </span>
                    ) : null}
                    <PanelRoleBadge role={p.isTeamLead ? "CSR Team Lead" : p.panelRole || "Chat Support"} />
                  </div>
                </td>
                <td className="px-3 py-2.5 text-[11px] text-gray-500 border-r border-gray-100 max-w-[220px]">
                  {summarizeTabs(p.tabPermissions)}
                </td>
                <td className="px-2 py-2.5 text-center font-bold text-gray-800 bg-red-50/20">
                  {c.total || 0}
                </td>
                {TICKET_STATUS_COLUMNS.map((col) => (
                  <td key={col.key} className="px-2 py-2.5 text-center border-r border-gray-50 bg-red-50/10">
                    <span
                      className={`inline-flex min-w-[1.75rem] justify-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${col.badge}`}
                    >
                      {c[col.key] || 0}
                    </span>
                  </td>
                ))}
                <td className="px-2 py-2.5 text-center font-bold text-gray-800 bg-cyan-50/20">
                  {p.chatSupportLog?.customerLogCount ?? 0}
                </td>
                <td className="px-2 py-2.5 text-center bg-cyan-50/20">
                  <span className="inline-flex min-w-[2.5rem] justify-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-100 text-cyan-800">
                    {p.chatSupportLog?.overallPercent ?? 0}%
                  </span>
                </td>
                {CHAT_LOG_COLUMNS.map((col) => (
                  <td key={col.key} className="px-2 py-2.5 text-center border-r border-gray-50">
                    <span
                      className={`inline-flex min-w-[1.75rem] justify-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${col.badge}`}
                    >
                      {p.chatSupportLog?.totals?.[col.key] ?? 0}
                    </span>
                  </td>
                ))}
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  const outreachColSpan = 3 + OUTREACH_COLUMNS.length;

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <table className="min-w-full text-xs md:text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2.5 text-left font-semibold text-gray-500 border-r border-gray-200">
              Agent
            </th>
            <th className="px-3 py-2.5 text-left font-semibold text-gray-500 border-r border-gray-200">
              Role
            </th>
            <th className="px-3 py-2.5 text-left font-semibold text-gray-500 border-r border-gray-200 min-w-[180px]">
              Can access
            </th>
            <th
              colSpan={outreachColSpan}
              className="px-3 py-2 text-center font-semibold text-gray-700 bg-teal-50/80"
            >
              Sale / CSR outreach
            </th>
          </tr>
          <tr>
            <th colSpan={3} className="border-r border-gray-200" />
            <th className="px-2 py-2 text-center font-semibold text-gray-500 bg-teal-50/50 border-r border-gray-100">
              Total
            </th>
            {OUTREACH_COLUMNS.map((col) => (
              <th
                key={col.key}
                className="px-2 py-2 text-center font-semibold text-gray-500 bg-teal-50/50 border-r border-gray-100"
              >
                {col.label}
              </th>
            ))}
            <th className="px-2 py-2 text-center font-semibold text-gray-500 bg-teal-50/50 border-r border-gray-100">
              Calls/mo
            </th>
            <th className="px-2 py-2 text-center font-semibold text-gray-500 bg-teal-50/50">
              Users messaged/mo
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.agentId} className="border-t border-gray-100 hover:bg-gray-50/50">
              <td className="px-3 py-2.5 border-r border-gray-100">
                <p className="font-medium text-gray-900 whitespace-nowrap">{p.name}</p>
                <p className="text-[10px] text-gray-400 break-all">{p.email}</p>
              </td>
              <td className="px-3 py-2.5 border-r border-gray-100">
                <div className="flex flex-wrap items-center gap-1">
                  {p.isTeamLead ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-indigo-100 text-indigo-800">
                      Team Lead
                    </span>
                  ) : null}
                  <PanelRoleBadge role={p.panelRole || (p.isTeamLead ? "CSR Team Lead" : "CSR")} />
                  {p.isFormerCsr ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-800">
                      Former
                    </span>
                  ) : null}
                </div>
              </td>
              <td className="px-3 py-2.5 text-[11px] text-gray-500 border-r border-gray-100 max-w-[220px]">
                {summarizeTabs(p.tabPermissions)}
              </td>
              <td className="px-2 py-2.5 text-center font-bold text-gray-800 bg-teal-50/20 border-r border-gray-50">
                {p.csrOutreachCounts?.total || 0}
              </td>
              {OUTREACH_COLUMNS.map((col) => (
                <td key={col.key} className="px-2 py-2.5 text-center border-r border-gray-50">
                  <span
                    className={`inline-flex min-w-[1.75rem] justify-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${col.badge}`}
                  >
                    {p.csrOutreachCounts?.[col.key] || 0}
                  </span>
                </td>
              ))}
              <td className="px-2 py-2.5 text-center border-r border-gray-50 font-semibold text-gray-800">
                {p.monthlyCalls || 0}
              </td>
              <td className="px-2 py-2.5 text-center font-semibold text-gray-800">
                {p.monthlyMessages || 0}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function teamLeadsForTeam(staff) {
  return staff.filter((s) => s.panelRole === "CSR Team Lead");
}

function agentsForTeam(staff, teamId = null, teamType = null) {
  return staff.filter((s) => {
    if (teamType === "chat_support") {
      if (s.panelRole !== "Chat Support") return false;
    } else if (teamType === "csr") {
      if (s.panelRole !== "CSR") return false;
    } else if (!MEMBER_PANEL_ROLES.has(s.panelRole)) {
      return false;
    }
    if (!s.memberTeamId && !s.teamName) return true;
    if (teamId && Number(s.memberTeamId) === Number(teamId)) return true;
    return false;
  });
}

function filterMemberIdsForTeamType(memberIds, staff, teamType) {
  const allowedRole = teamType === "chat_support" ? "Chat Support" : "CSR";
  const staffMap = Object.fromEntries(staff.map((s) => [String(s.id), s]));
  return memberIds.filter((id) => staffMap[id]?.panelRole === allowedRole);
}

function buildMemberPermissionsPayload(memberPermissions) {
  const out = {};
  Object.entries(memberPermissions || {}).forEach(([id, perms]) => {
    out[id] = perms;
  });
  return out;
}

export default function SupportTeamsTab({
  staff = [],
  teams = [],
  loading = false,
  isAdmin = false,
  isDeveloper = false,
  isCsrTeamLead = false,
  canViewCsrStaffList = false,
  canCreateCsrUser = false,
  currentAdminId = null,
  onRefresh,
}) {
  const canManageTeams = isAdmin || isCsrTeamLead;
  const [staffSearch, setStaffSearch] = useState("");
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSearch, setCreateSearch] = useState("");
  const [teamForm, setTeamForm] = useState(emptyTeamForm);
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [highlightLeadRequired, setHighlightLeadRequired] = useState(false);

  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editForm, setEditForm] = useState(emptyTeamForm);
  const [editSearch, setEditSearch] = useState("");
  const [updatingTeam, setUpdatingTeam] = useState(false);
  const [deletingTeamId, setDeletingTeamId] = useState(null);
  const [activeTeamId, setActiveTeamId] = useState(null);

  useEffect(() => {
    if (!teams.length) {
      setActiveTeamId(null);
      return;
    }
    setActiveTeamId((current) => {
      if (current && teams.some((t) => t.id === current)) return current;
      return teams[0].id;
    });
  }, [teams]);

  const myLedTeams = teams.filter((t) => Number(t.teamLead?.id) === Number(currentAdminId));
  const myLedTeam = myLedTeams[0];

  const activeTeam = useMemo(
    () => teams.find((t) => t.id === activeTeamId) || null,
    [teams, activeTeamId]
  );

  const supportStaff = useMemo(
    () => staff.filter((s) => SUPPORT_PANEL_ROLES.has(s.panelRole)),
    [staff]
  );

  const filteredStaff = useMemo(() => {
    const q = staffSearch.trim().toLowerCase();
    if (!q) return supportStaff;
    return supportStaff.filter(
      (s) =>
        `${s.firstName} ${s.lastName}`.toLowerCase().includes(q) ||
        (s.email || "").toLowerCase().includes(q) ||
        (s.panelRole || "").toLowerCase().includes(q) ||
        (s.supportRoleLabel || "").toLowerCase().includes(q)
    );
  }, [supportStaff, staffSearch]);

  const createUserAllowedRoles = isAdmin
    ? ["CSR", "Chat Support", "CSR Team Lead"]
    : ["CSR", "Chat Support"];

  const createLeadChoices = useMemo(() => {
    if (isCsrTeamLead && !isAdmin && currentAdminId) {
      return staff.filter((s) => Number(s.id) === Number(currentAdminId));
    }
    return teamLeadsForTeam(staff);
  }, [staff, isCsrTeamLead, isAdmin, currentAdminId]);

  const createMemberChoices = useMemo(
    () => agentsForTeam(staff, null, teamForm.team_type),
    [staff, teamForm.team_type]
  );

  const editLeadChoices = useMemo(() => {
    if (isCsrTeamLead && !isAdmin && currentAdminId) {
      return staff.filter((s) => Number(s.id) === Number(currentAdminId));
    }
    return teamLeadsForTeam(staff);
  }, [staff, isCsrTeamLead, isAdmin, currentAdminId]);

  const editMemberChoices = useMemo(
    () => agentsForTeam(staff, editingTeamId, editForm.team_type),
    [staff, editingTeamId, editForm.team_type]
  );

  useEffect(() => {
    if (isCsrTeamLead && !isAdmin && createOpen && currentAdminId) {
      setTeamForm((f) => ({ ...f, team_lead_id: String(currentAdminId) }));
    }
  }, [isCsrTeamLead, isAdmin, createOpen, currentAdminId]);

  const updateStaffRole = async (userId, support_role) => {
    try {
      await supportService.updateStaffRole(userId, support_role);
      toast.success("Support role updated");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update role");
    }
  };

  const toggleTeamMember = (userId, formSetter) => {
    const id = String(userId);
    formSetter((f) => {
      const nextIds = f.member_ids.includes(id)
        ? f.member_ids.filter((m) => m !== id)
        : [...f.member_ids, id];
      const nextPerms = { ...f.member_permissions };
      if (!nextIds.includes(id)) delete nextPerms[id];
      return { ...f, member_ids: nextIds, member_permissions: nextPerms };
    });
  };

  const teamPayloadFromForm = (form) => ({
    name: form.name.trim(),
    team_type: form.team_type || "csr",
    team_lead_id: Number(form.team_lead_id),
    member_ids: form.member_ids.map(Number),
    lead_tab_permissions: form.lead_tab_permissions,
    member_tab_permissions: form.member_tab_permissions,
    chat_support_tab_permissions: form.chat_support_tab_permissions,
    member_permissions: buildMemberPermissionsPayload(form.member_permissions),
  });

  const setTeamTypeOnForm = (setForm, teamType) => {
    setForm((f) => ({
      ...f,
      team_type: teamType,
      member_ids: filterMemberIdsForTeamType(f.member_ids, staff, teamType),
      member_permissions: Object.fromEntries(
        Object.entries(f.member_permissions || {}).filter(([id]) =>
          filterMemberIdsForTeamType([id], staff, teamType).includes(id)
        )
      ),
    }));
  };

  const createTeam = async () => {
    if (!teamForm.name?.trim()) {
      toast.error("Team name is required");
      return;
    }
    if (!teamForm.team_lead_id) {
      setHighlightLeadRequired(true);
      toast.error("Select a team lead using the Lead radio button (CSR Team Lead required).");
      return;
    }
    setHighlightLeadRequired(false);
    if (creatingTeam) return;
    try {
      setCreatingTeam(true);
      await supportService.createTeam(teamPayloadFromForm(teamForm));
      toast.success("Team created");
      setTeamForm(emptyTeamForm());
      setCreateOpen(false);
      setCreateSearch("");
      onRefresh?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to create team"
      );
    } finally {
      setCreatingTeam(false);
    }
  };

  const startEditTeam = (team) => {
    // Only treat as per-member override when tabs differ from the role default for this team
    const memberPerms = {};
    const csrDefault =
      team.memberTabPermissions || team.permissions?.member || DEFAULT_MEMBER_PERMS;
    const chatDefault =
      team.chatSupportTabPermissions ||
      team.permissions?.chatSupport ||
      DEFAULT_CHAT_SUPPORT_PERMS;
    (team.members || []).forEach((m) => {
      if (!m.tabPermissions) return;
      const roleDefault = m.panelRole === "Chat Support" ? chatDefault : csrDefault;
      const same = TAB_PERM_LABELS.every(
        (t) => !!m.tabPermissions[t.key] === !!roleDefault[t.key]
      );
      if (!same) {
        memberPerms[String(m.id)] = { ...m.tabPermissions };
      }
    });
    setEditingTeamId(team.id);
    setActiveTeamId(team.id);
    setEditForm({
      name: team.name,
      team_type: inferTeamPerformanceKind(team),
      team_lead_id: String(team.teamLead?.id || ""),
      member_ids: (team.members || []).map((m) => String(m.id)),
      lead_tab_permissions: team.leadTabPermissions || team.permissions?.lead || { ...DEFAULT_LEAD_PERMS },
      member_tab_permissions: { ...csrDefault },
      chat_support_tab_permissions: { ...chatDefault },
      member_permissions: memberPerms,
    });
    setEditSearch("");
  };

  const cancelEditTeam = () => {
    setEditingTeamId(null);
    setEditForm(emptyTeamForm());
    setEditSearch("");
  };

  const saveEditTeam = async () => {
    if (!editingTeamId || !editForm.name?.trim() || !editForm.team_lead_id) {
      toast.error("Team name and team lead are required");
      return;
    }
    if (updatingTeam) return;
    try {
      setUpdatingTeam(true);
      const payload = teamPayloadFromForm(editForm);
      if (isCsrTeamLead && !isAdmin) {
        delete payload.team_lead_id;
      }
      await supportService.updateTeam(editingTeamId, payload);
      toast.success("Team updated");
      cancelEditTeam();
      onRefresh?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to update team"
      );
    } finally {
      setUpdatingTeam(false);
    }
  };

  const deleteTeam = async (team) => {
    const ok = await confirmDanger(
      `Delete team "${team.name}"? Members will be unassigned from this team.`,
      "Delete support team?"
    );
    if (!ok) return;
    try {
      setDeletingTeamId(team.id);
      await supportService.deleteTeam(team.id);
      toast.success("Team deleted");
      if (editingTeamId === team.id) cancelEditTeam();
      onRefresh?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to delete team"
      );
    } finally {
      setDeletingTeamId(null);
    }
  };

  const canEditThisTeam = (team) => {
    if (isAdmin) return true;
    if (isCsrTeamLead && Number(team.teamLead?.id) === Number(currentAdminId)) return true;
    return false;
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <CardsSkeleton count={3} cols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" />
        <TableSkeleton rows={6} cols={5} />
      </div>
    );
  }

  const renderTeamFormBody = ({
    form,
    setForm,
    leadChoices,
    memberChoices,
    search,
    setSearch,
    leadLocked,
    emptyMessage,
    showLeadRequiredHint = false,
    onLeadHighlightClear,
    teamTypeLocked = false,
  }) => (
    <>
      <TeamTypeSelector
        value={form.team_type || "csr"}
        onChange={(team_type) => setTeamTypeOnForm(setForm, team_type)}
        disabled={teamTypeLocked}
      />
      <input
        placeholder={form.team_type === "chat_support" ? "Team name (e.g. Chat Support)" : "Team name (e.g. Sale Team)"}
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        className={`${inputCls} w-full max-w-md mb-4`}
      />
      {showLeadRequiredHint && !form.team_lead_id && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-medium">Team lead required</p>
          <p className="text-xs mt-1 text-amber-800">
            {leadChoices.length === 0
              ? "Create a CSR Team Lead user above, then select them as team lead."
              : "Choose one CSR Team Lead in the Lead column. The same lead can manage multiple teams."}
          </p>
        </div>
      )}
      <MemberPickerTable
        teamLeadChoices={leadChoices}
        memberChoices={memberChoices}
        teamLeadId={form.team_lead_id}
        memberIds={form.member_ids}
        onLeadChange={(id) => {
          onLeadHighlightClear?.();
          setForm((f) => ({
            ...f,
            team_lead_id: String(id),
            member_ids: f.member_ids.filter((m) => m !== String(id)),
          }));
        }}
        onToggleMember={(id) => toggleTeamMember(id, setForm)}
        search={search}
        onSearchChange={setSearch}
        emptyMessage={
          emptyMessage ||
          (form.team_type === "chat_support"
            ? "Add Chat Support users first, then select them as members."
            : "Add CSR users first, then select them as members.")
        }
        leadLocked={leadLocked}
        highlightLeadRequired={showLeadRequiredHint && !form.team_lead_id}
      />
      <div className="mt-4 space-y-4">
        <RoleTabEditors form={form} setForm={setForm} teamType={form.team_type || "csr"} />
        <MemberTabOverrides
          memberIds={form.member_ids}
          staff={staff}
          memberPermissions={form.member_permissions}
          onChangeMemberPerms={(member_permissions) =>
            setForm((f) => ({ ...f, member_permissions }))
          }
        />
      </div>
    </>
  );

  return (
    <div className="space-y-5">
      {canViewCsrStaffList && (
        <SectionCard
          title="Users — Roles"
          subtitle="CSR, Chat Support, and CSR Team Lead users. Create members here, then assign them to a team with tab access below."
          icon={FiUserCheck}
          action={
            canCreateCsrUser ? (
              <button
                type="button"
                onClick={() => setCreateUserOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white shadow-sm hover:opacity-90"
                style={{ backgroundColor: THEME.primary }}
              >
                <FiPlus /> Create User
              </button>
            ) : null
          }
        >
          <input
            type="text"
            placeholder="Search by name, email, or role..."
            value={staffSearch}
            onChange={(e) => setStaffSearch(e.target.value)}
            className={`${inputCls} w-full max-w-md mb-4`}
          />
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full text-xs md:text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2.5 text-left font-semibold text-gray-500">Name</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-gray-500">Email</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-gray-500">Panel Role</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-gray-500">Support Role</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-gray-500">Team</th>
                  {isAdmin && (
                    <th className="px-3 py-2.5 text-left font-semibold text-gray-500">Set Role</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredStaff.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50/80">
                    <td className="px-3 py-2.5 font-medium text-gray-900">
                      {u.firstName} {u.lastName}
                    </td>
                    <td className="px-3 py-2.5 text-gray-600">{u.email}</td>
                    <td className="px-3 py-2.5">
                      <PanelRoleBadge role={u.panelRole} />
                    </td>
                    <td className="px-3 py-2.5">
                      <SupportRoleBadge role={u.supportRole} label={u.supportRoleLabel} />
                    </td>
                    <td className="px-3 py-2.5 text-gray-500">
                      {u.teamName || "—"}
                      {u.isTeamLead && (
                        <span className="ml-1 text-[10px] text-indigo-600 font-semibold">(Lead)</span>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2.5">
                        <select
                          value={u.supportRole}
                          onChange={(e) => updateStaffRole(u.id, e.target.value)}
                          className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white"
                        >
                          <option value="NONE">Not in support</option>
                          <option value="AGENT">Agent</option>
                          <option value="TEAM_LEAD">Team Lead</option>
                          <option value="SUPERVISOR">Supervisor</option>
                        </select>
                      </td>
                    )}
                  </tr>
                ))}
                {!filteredStaff.length && (
                  <tr>
                    <td colSpan={isAdmin ? 6 : 5} className="px-3 py-8 text-center text-gray-400">
                      No support users found.{" "}
                      {canCreateCsrUser ? "Create a CSR or Chat Support user to get started." : ""}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {isCsrTeamLead && !myLedTeam && !isAdmin && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          You are not assigned as lead on a support team yet. Create a new team below (with yourself as lead), or ask an Admin to assign you.
        </div>
      )}

      {canManageTeams && (
        <SectionCard
          title="Create Team"
          subtitle="Pick team type first (CSR/Sale or Chat Support), then assign the same team lead to multiple teams with separate performance."
          icon={FiPlus}
          accent="rose"
          action={
            <button
              type="button"
              onClick={() => setCreateOpen((o) => !o)}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
            >
              {createOpen ? "Hide form" : "New team"}
            </button>
          }
        >
          {teams.length > 0 && (
            <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              <p className="font-medium">One team lead, separate team types</p>
              <p className="text-xs mt-1 text-blue-800">
                Choose <span className="font-semibold">Chat Support Team</span> below, select Kaynat as
                lead, and add chat agents. Sale Team stays CSR-only — each team tab shows its own
                performance (not mixed).
              </p>
            </div>
          )}
          {createOpen ? (
            <>
              {renderTeamFormBody({
                form: teamForm,
                setForm: setTeamForm,
                leadChoices: createLeadChoices,
                memberChoices: createMemberChoices,
                search: createSearch,
                setSearch: setCreateSearch,
                leadLocked: isCsrTeamLead && !isAdmin,
                emptyMessage: "Add CSR Team Lead, CSR, and Chat Support users first.",
                showLeadRequiredHint: highlightLeadRequired,
                onLeadHighlightClear: () => setHighlightLeadRequired(false),
              })}
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  type="button"
                  onClick={createTeam}
                  disabled={creatingTeam}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-sm hover:opacity-90 disabled:opacity-60 min-w-[140px]"
                  style={{ backgroundColor: THEME.primary }}
                >
                  {creatingTeam ? (
                    <>
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FiPlus /> Create Team
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCreateOpen(false);
                    setTeamForm(emptyTeamForm());
                    setCreateSearch("");
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50"
                >
                  <FiX /> Cancel
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              Click <span className="font-semibold text-gray-700">New team</span> to assign a CSR Team
              Lead with CSR and Chat Support members.
            </p>
          )}
        </SectionCard>
      )}

      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            {teams.length
              ? `${teams.length}] Team${teams.length > 1 ? "s" : ""}`
              : "Teams"}
          </h3>
        </div>

        {teams.length > 0 && (
          <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-1">
            {teams.map((team) => {
              const isActive = team.id === activeTeamId;
              return (
                <button
                  key={team.id}
                  type="button"
                  onClick={() => {
                    setActiveTeamId(team.id);
                    if (editingTeamId && editingTeamId !== team.id) {
                      cancelEditTeam();
                    }
                  }}
                  className={`inline-flex items-center gap-2 rounded-t-lg border px-4 py-2.5 text-sm font-semibold transition-colors ${
                    isActive
                      ? "border-gray-200 border-b-white bg-white text-gray-900 shadow-sm -mb-px"
                      : "border-transparent bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                  }`}
                >
                  <FiUsers className="shrink-0" style={{ color: isActive ? THEME.primary : undefined }} />
                  {team.name}
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                      inferTeamPerformanceKind(team) === "chat_support"
                        ? "bg-cyan-100 text-cyan-800"
                        : "bg-teal-100 text-teal-800"
                    }`}
                  >
                    {inferTeamPerformanceKind(team) === "chat_support" ? "Chat" : "CSR"}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      isActive ? "bg-rose-100 text-rose-700" : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {(team.members?.length || 0) + 1}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {activeTeam && (() => {
          const team = activeTeam;
          const isEditing = editingTeamId === team.id;
          const allowEdit = canEditThisTeam(team);
          const teamKind = inferTeamPerformanceKind(team);
          const performanceLabel =
            teamKind === "chat_support"
              ? "Chat support performance (team lead + members)"
              : "Sale / CSR outreach performance (team lead + members)";
          return (
            <div
              className={`rounded-2xl border bg-white shadow-sm overflow-hidden transition-shadow ${
                isEditing ? "border-rose-200 ring-2 ring-rose-100" : "border-gray-200"
              }`}
            >
              <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {team.name}
                    <span className="text-sm font-normal text-gray-500 mx-2">·</span>
                    <span className="text-sm font-semibold" style={{ color: THEME.primary }}>
                      Lead: {team.teamLead?.name}
                    </span>
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">{team.teamLead?.email}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Members ({team.members?.length || 0}):{" "}
                    {team.members?.length
                      ? team.members
                          .map((m) => `${m.name}${m.panelRole ? ` (${m.panelRole})` : ""}`)
                          .join(", ")
                      : "None"}
                  </p>
                  {!isEditing && (
                    <p className="text-[11px] text-gray-400 mt-2">
                      Lead: {summarizeTabs(team.leadTabPermissions)}
                      {teamKind === "chat_support" ? (
                        <> · Chat Support: {summarizeTabs(team.chatSupportTabPermissions)}</>
                      ) : (
                        <> · CSR: {summarizeTabs(team.memberTabPermissions)}</>
                      )}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {allowEdit && !isEditing && (
                    <button
                      type="button"
                      onClick={() => startEditTeam(team)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 bg-gray-50 border border-gray-200 hover:bg-gray-100"
                    >
                      <FiEdit2 /> Update Team
                    </button>
                  )}
                  {isDeveloper && (
                    <button
                      type="button"
                      onClick={() => deleteTeam(team)}
                      disabled={deletingTeamId === team.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 disabled:opacity-50"
                    >
                      {deletingTeamId === team.id ? (
                        <>
                          <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <FiTrash2 /> Delete
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {isEditing && allowEdit && (
                <div className="px-5 py-4 bg-rose-50/40 border-b border-rose-100">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FiEdit2 style={{ color: THEME.primary }} /> Update Team
                  </h4>
                  {renderTeamFormBody({
                    form: editForm,
                    setForm: setEditForm,
                    leadChoices: editLeadChoices,
                    memberChoices: editMemberChoices,
                    search: editSearch,
                    setSearch: setEditSearch,
                    leadLocked: isCsrTeamLead && !isAdmin,
                    emptyMessage: "No members available for this team type.",
                    teamTypeLocked: true,
                  })}
                  <div className="flex flex-wrap gap-2 mt-4">
                    <button
                      type="button"
                      onClick={saveEditTeam}
                      disabled={updatingTeam}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-sm hover:opacity-90 disabled:opacity-60 min-w-[140px]"
                      style={{ backgroundColor: THEME.primary }}
                    >
                      {updatingTeam ? (
                        <>
                          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FiSave /> Save Changes
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditTeam}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50"
                    >
                      <FiX /> Cancel
                    </button>
                  </div>
                </div>
              )}

              <div className="p-5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <FiUsers /> {performanceLabel}
                </p>
                <TeamMemberStatsTable performance={team.performance} teamType={teamKind} />
              </div>
            </div>
          );
        })()}

        {!teams.length && (
          <p className="text-gray-500 text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            No teams configured yet.{" "}
            {canManageTeams ? "Create a support team above." : "Ask an Admin to set up teams."}
          </p>
        )}
      </div>

      <AdminPanelUserModal
        isOpen={createUserOpen}
        onClose={() => setCreateUserOpen(false)}
        onSuccess={() => onRefresh?.()}
        allowedRoles={createUserAllowedRoles}
        defaultRole="CSR"
        createViaSupport
        modalTitle="Create Support User"
        showTabPermissions
      />
    </div>
  );
}
